import type EscPosEncoder from '@/lib/print/EscPosEncoder';
import type { ICajaResume } from '@/interfaces/ICajaResume';
import { getCanceledItemsOmittedUnits } from '@/domain/caja/caja-resume-canceled-items';

export interface ICajaCanceledItemsPrintFormatters {
  formatPrice: (price: number) => string;
  formatDateShort: (date?: string | Date | number) => string;
}

/**
 * ITEMS CANCELADOS block of the caja resume ticket: one entry per cancelled
 * line inside finished orders, printed right after PEDIDOS CANCELADOS (the
 * preceding separator already exists). Emits nothing for snapshots stored
 * before the list existed.
 */
export function printCajaResumeCanceledItems(
  encoder: EscPosEncoder,
  resume: Pick<ICajaResume, 'ordersTotals' | 'canceledItemsDetails'>,
  formatters: ICajaCanceledItemsPrintFormatters
): void {
  const lines = resume.canceledItemsDetails;
  if (!lines?.length) return;

  // Set the size first: the separator width follows the current size, so the
  // block renders identically whatever size the previous section left.
  encoder.size('small');
  encoder.boldLine('ITEMS CANCELADOS');
  encoder.separator();

  for (const line of lines) {
    encoder.keyValueSpaceBetween(
      `${line.quantity}x ${line.itemName}`,
      formatters.formatPrice(line.total)
    );
    const tablePrefix = line.tableName ? `Mesa ${line.tableName} - ` : '';
    encoder.line(
      `${tablePrefix}Pedido ${line.orderRef} - ${formatters.formatDateShort(
        line.canceledAt
      )}`
    );
  }

  const omittedUnits = getCanceledItemsOmittedUnits(resume);
  if (omittedUnits > 0) {
    const noun = omittedUnits === 1 ? 'producto' : 'productos';
    encoder.line(`y ${omittedUnits} ${noun} más sin detalle`);
  }

  encoder.size('normal');
  encoder.separator();
}
